import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ImageOff, AlertTriangle, Box as BoxIcon } from 'lucide-react';
import { cn, useToast, FurnitureIcon } from '@easymoving/ui';
import type { Destination, Item, ProgressStatus } from '@easymoving/types';
import { useBackend } from '../../lib/backend/context';
import { categoryLabel, roomLabel } from '../../lib/taxonomy/options';
import { DestinationSelect, StatusSelect } from './QuickSelects';
import { itemVisual } from '../../lib/items/visual';
import { itemLabel } from '../../lib/items/label';

/**
 * Compact list row for the gallery's table view — a thumbnail + name that
 * links to the detail page, plus inline destination/status selects for quick
 * editing without leaving the list (writes straight through the backend).
 */
export function ItemRow({
  item,
  selected,
  onToggleSelect,
}: {
  item: Item;
  selected?: boolean;
  onToggleSelect?: (id: string) => void;
}) {
  const { t } = useTranslation();
  const backend = useBackend();
  const toast = useToast();
  const visual = itemVisual(item, { thumb: true });
  const label = itemLabel(item);
  const sub = [roomLabel(item.room, t), categoryLabel(item.category, t)].filter(Boolean).join(' · ');

  const quickUpdate = (patch: Parameters<typeof backend.updateItem>[1]) =>
    void backend.updateItem(item.id, patch).then(() => toast.push({ title: t('item.updated'), tone: 'success' }));

  return (
    <li className={cn('surface flex flex-col gap-2.5 p-2.5', selected && 'ring-2 ring-petrol')}>
      <div className="flex items-center gap-3 min-w-0">
        {onToggleSelect && (
          <input
            type="checkbox"
            checked={!!selected}
            onChange={() => onToggleSelect(item.id)}
            aria-label={`${t('bulk.select')} — ${label}`}
            className="size-5 shrink-0 accent-[var(--color-petrol)]"
          />
        )}
        <Link to={`/app/items/${item.id}`} className="flex items-center gap-3 min-w-0 flex-1">
        <span className="size-12 shrink-0 rounded-md overflow-hidden bg-surface-tint flex items-center justify-center">
          {visual.kind === 'photo' ? (
            <img src={visual.url} alt="" className="w-full h-full object-cover" />
          ) : visual.kind === 'icon' ? (
            <span className="w-full h-full flex items-center justify-center bg-petrol-tint">
              <FurnitureIcon type={visual.iconType} className="size-8" />
            </span>
          ) : (
            <ImageOff className="size-5 text-muted" />
          )}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1.5">
            <span className="font-medium text-[15px] leading-tight line-clamp-1">{label}</span>
            {item.quantity > 1 && <span className="text-graphite-soft text-[12px] tnum">×{item.quantity}</span>}
            {item.fragile && <AlertTriangle className="size-3.5 text-amber shrink-0" />}
            {item.boxId && <BoxIcon className="size-3.5 text-graphite-soft shrink-0" />}
          </span>
          {sub && <span className="block text-[12px] text-graphite-soft line-clamp-1">{sub}</span>}
        </span>
        </Link>
      </div>

      <div className="flex flex-wrap items-center gap-2 ps-9">
        <DestinationSelect
          compact
          ariaLabel={`${t('item.destination')} — ${label}`}
          value={item.destination}
          onValueChange={(v) => quickUpdate({ destination: v as Destination })}
        />
        <StatusSelect
          compact
          ariaLabel={`${t('item.status')} — ${label}`}
          value={item.progressStatus}
          onValueChange={(v) => quickUpdate({ progressStatus: v as ProgressStatus })}
        />
      </div>
    </li>
  );
}
